package com.wintermar.inspection.navigation

import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.lifecycle.viewmodel.navigation3.rememberViewModelStoreNavEntryDecorator
import androidx.navigation3.runtime.NavBackStack
import androidx.navigation3.runtime.NavKey
import androidx.navigation3.runtime.entryProvider
import androidx.navigation3.runtime.rememberNavBackStack
import androidx.navigation3.runtime.rememberSaveableStateHolderNavEntryDecorator
import androidx.navigation3.ui.NavDisplay
import androidx.savedstate.serialization.SavedStateConfiguration
import com.wintermar.inspection.core.data.AppSetting
import com.wintermar.inspection.core.ui.widget.PageNotFound
import com.wintermar.inspection.feature.presentation.splash.SplashScreen
import com.wintermar.inspection.navigation.feature.AuthNavigation
import com.wintermar.inspection.navigation.feature.InspectionNavigation
import com.wintermar.inspection.navigation.feature.TemplateNavigation
import com.wintermar.inspection.util.global_navigation.GlobalNavigationBus
import com.wintermar.inspection.util.global_navigation.NavigationEvent
import kotlinx.serialization.modules.SerializersModule
import kotlinx.serialization.modules.polymorphic
import org.koin.compose.koinInject

@Composable
fun NavigationRoot(
    initialBackstack: Route,
    paddingValues: PaddingValues,
    onVisibilityChanged: (showTopBar: Boolean) -> Unit,
    onControllerReady: (NavigationController) -> Unit,
    navigationBus: GlobalNavigationBus = koinInject(),
    appSetting: AppSetting = koinInject()
) {
    var initialInspectionBackStack by remember {
        mutableStateOf<Route?>(null)
    }
    val rootBackStack = rememberNavBackStack(
        configuration = SavedStateConfiguration {
            serializersModule = SerializersModule {
                polymorphic(NavKey::class) {
                    subclass(Route.Auth::class, Route.Auth.serializer())
                    subclass(Route.Splash::class, Route.Splash.serializer())
                    subclass(Route.Template::class, Route.Template.serializer())
                    subclass(Route.Inspection::class, Route.Inspection.serializer())
                    subclass(Route.NotFound::class, Route.NotFound.serializer())
                    subclass(Route.Logout::class, Route.Logout.serializer())
                }
            }
        },
        initialBackstack
    )

    val controller = remember(rootBackStack) {
        object : NavigationController {
            override fun navigateTo(
                route: Route,
                removeAllExistingRoute: Boolean
            ) {
                if (removeAllExistingRoute) {
                    rootBackStack.clear()
                }
                rootBackStack.add(route)
            }

            override val currentRoute: Route? get() = rootBackStack.lastOrNull() as? Route
        }
    }

    LaunchedEffect(navigationBus) {
        navigationBus.events.collect { event ->
            if (event is NavigationEvent.Logout) handleForceLogoutPlatform(controller)
        }
    }

    BindNavigationToPlatform(
        rootBackStack = rootBackStack,
        currentRoute = controller.currentRoute
    )

    LaunchedEffect(controller) {
        onControllerReady(controller)
    }

    LaunchedEffect(controller.currentRoute) {
        when (controller.currentRoute) {
            Route.Template,
            Route.Inspection,
            Route.Logout -> onVisibilityChanged(true)

            else -> onVisibilityChanged(false)
        }
    }

    NavDisplay(
        backStack = rootBackStack,
        entryDecorators = listOf(
            rememberSaveableStateHolderNavEntryDecorator(),
            rememberViewModelStoreNavEntryDecorator()
        ),
        entryProvider = entryProvider {
            entry<Route.Splash> {
                SplashScreen(
                    onLogin = {
                        rootBackStack.remove(Route.Splash)
                        rootBackStack.add(Route.Auth)
                    },
                    onDoneInitialization = {
                        rootBackStack.remove(Route.Splash)
                        rootBackStack.add(
                            when {
                                appSetting.templateAccess == true -> Route.Template
                                appSetting.inspectionAccess == true -> Route.Inspection
                                else -> Route.Logout
                            }
                        )
                    }
                )
            }
            entry<Route.Auth> {
                AuthNavigation(
                    onLogin = {
                        rootBackStack.remove(Route.Auth)
                        rootBackStack.add(Route.Splash)
                    }
                )
            }
            entry<Route.Template> {
                TemplateNavigation(
                    paddingValues = paddingValues,
                    onVisibilityChanged = {
                        onVisibilityChanged(it)
                    },
                    onStartInspection = { inspectionId ->
                        initialInspectionBackStack = Route.Inspection.Answer(inspectionId)
                        rootBackStack.add(Route.Inspection)
                        rootBackStack.remove(Route.Template)
                    }
                )
            }
            entry<Route.Inspection> {
                InspectionNavigation(
                    paddingValues = paddingValues,
                    initialBackStack = initialInspectionBackStack,
                    onVisibilityChanged = onVisibilityChanged,
                    onResetInitialBackStack = {
                        initialInspectionBackStack = null
                    }
                )

            }
            entry<Route.NotFound> {
                PageNotFound()
            }
            entry<Route.Logout> {
                PageNotFound()
            }
        }
    )
}

@Composable
expect fun BindNavigationToPlatform(
    rootBackStack: NavBackStack<NavKey>,
    currentRoute: Route?
)

expect fun handleForceLogoutPlatform(controller: NavigationController)
